/**
 * "Force scenes open on Board tab" (20260909, owner) — when ON, every scene on the Board renders
 * expanded: no disclosure, no hidden overflow row, and the header's Edit coverage door stops
 * waiting for a selection. Default OFF — except on macOS, where the owner defaulted it ON
 * (20260922); his own framing is still "I'm not sure we'll keep this behavior", so it is a
 * preference with a real off state, not a migration. See defaultForceScenesOpen below.
 *
 * NATIVE-AUTHORITATIVE, a structural copy of theme/tileSizing.ts for a BOOLEAN — read that
 * module's header for the full rationale; the short version:
 *   - EMBEDDED: Unity owns the value. It appends ?forceScenesOpen=1|0 on every webview load
 *     (boot), ships the row in the setPreferences catalog (applyForceScenesOpenFromPreferences),
 *     and receives every change through the ordinary setPreference verb.
 *   - STANDALONE / online review: no bridge, so localStorage is the sole store.
 * localStorage is written in BOTH modes but is only ever a MIRROR: the board webview's teardown
 * calls UniWebView CleanCache(), which drops WKWebsiteDataTypeLocalStorage app-wide, so a value
 * that lives only there is one project-exit away from being lost.
 *
 * The wire form is deliberately WIDER than tileGrowth's: the URL param carries '1'/'0' (what
 * GetThemeQueryFragment appends for a bool) while the catalog row is a real JSON boolean, and a
 * setPreference echo can come back as 'true'/'false'. All four are accepted; anything else is
 * "no answer" and leaves the live value alone.
 */

import { getHostInput } from '@/platform/inputCapabilities';

/** localStorage mirror key. */
export const FORCE_SCENES_OPEN_STORAGE_KEY = 'timeline-force-scenes-open';

/** Query param Unity appends to every embedded webview load — the boot half of native authority
 *  (the catalog cannot serve boot: it is only pushed on requestPreferences). Value is '1' or '0'. */
export const FORCE_SCENES_OPEN_PARAM = 'forceScenesOpen';

/**
 * The default when NOBODY has answered — no boot param, no mirror. ON for the macOS app, OFF
 * everywhere else (owner, 20260922: "For macOS only, default the Force scenes open on board tab
 * preference to true").
 *
 * PLATFORM, NOT FORM FACTOR: the discriminator is the host's own `?platform=macos`, read through
 * platform/inputCapabilities.ts — the same parse the auto-focus policy and the Preferences modal's
 * per-platform rows already use, so there is no second detector to keep in step. A Mac BROWSER
 * (standalone, online review, the debug harness) carries no platform param and so is not macOS
 * here, which is right: this is a preference of the app, not of the operating system.
 *
 * Read per call rather than captured at module load, because it is only ever consulted as the LAST
 * fallback — after the param and after the mirror — so a stored answer always wins over it and a
 * later platform read can never re-default a value somebody has already set.
 *
 * NOTE (20260922): in EMBEDDED mode this default is unreachable, because Unity appends
 * ?forceScenesOpen=1|0 to every board load from its OWN stored preference. That native default
 * (SystemPreferencesManager.DefaultSystemPreferenceForceScenesOpenPreferenceType) is gated the same
 * way — On for the macOS player/Editor, Off elsewhere — so the two agree; this one covers every route
 * that boots WITHOUT the param, and is where the web's answer lives if the param ever stops coming.
 */
function defaultForceScenesOpen(): boolean {
  return getHostInput().platform === 'macos';
}

let currentForceScenesOpen: boolean = defaultForceScenesOpen();
const listeners = new Set<() => void>();

/** A recognised value, or null for "no answer" — an unset param, a value from a Unity build that
 *  predates this row, or junk. Null NEVER means the default: callers keep what they have, so an
 *  older native side can't silently switch the preference off under the user.
 *
 *  Booleans are accepted as well as the three string spellings because this one coercer serves
 *  three wires at once: the catalog row (a JSON bool), the boot URL param ('1'/'0') and the
 *  'true'/'false' form setPreference speaks. */
function coerceForceScenesOpen(value: unknown): boolean | null {
  if (value === true || value === false) return value;
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return null;
}

/** Best-effort re-seed of the localStorage mirror from live state. */
function writeMirror(enabled: boolean): void {
  try {
    localStorage.setItem(FORCE_SCENES_OPEN_STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    /* storage unavailable (private mode, post-CleanCache) — the in-session value still applies */
  }
}

function getStoredForceScenesOpen(): boolean | null {
  try {
    return coerceForceScenesOpen(localStorage.getItem(FORCE_SCENES_OPEN_STORAGE_KEY));
  } catch {
    return null;
  }
}

function readParam(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/**
 * Apply the forceScenesOpen row of Unity's setPreferences catalog — the authoritative value,
 * pushed when the Preferences panel opens and re-pushed after every setPreference write.
 *
 * Unrecognised input is IGNORED rather than defaulted (see coerceForceScenesOpen): a catalog from
 * a Unity build without this row leaves the preference untouched, which is what keeps the web
 * bundle safe to ship ahead of the native side. The mirror is re-seeded even when nothing changed
 * — that repair is the whole point after a CleanCache() wipe — but subscribers are only notified
 * on an actual change, so the catalog re-push that follows our own setPreference is a no-op.
 */
export function applyForceScenesOpenFromPreferences(raw: unknown): void {
  const next = coerceForceScenesOpen(raw) ?? currentForceScenesOpen;
  writeMirror(next);
  if (next === currentForceScenesOpen) return;
  currentForceScenesOpen = next;
  listeners.forEach((fn) => fn());
}

/** Current preference for the Board. */
export function getForceScenesOpen(): boolean {
  return currentForceScenesOpen;
}

/** Subscribe to changes (useSyncExternalStore-shaped). */
export function subscribeForceScenesOpen(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** User-facing setter: persists the mirror and notifies subscribers. The authoritative write
 *  (setPreference) is the caller's job — see PreferencesModal's dual-layer comment. */
export function setForceScenesOpen(enabled: boolean): void {
  if (enabled !== true && enabled !== false) return;
  currentForceScenesOpen = enabled;
  writeMirror(enabled);
  listeners.forEach((fn) => fn());
}

/**
 * Re-read the mirror and reconcile. This app runs TWO long-lived same-origin documents (the
 * kept-alive Projects webview is parked while the board runs, and restored WITHOUT a reload), so
 * a document's boot-time cache goes stale when the OTHER document changes the preference. Wired
 * to the cross-document 'storage' event, to visibility/focus recovery, and to
 * useForceScenesOpen() mount (the Preferences modal mounts fresh on every open).
 *
 * A MISSING key is a WIPE, not a reset to defaults: Unity's board teardown calls CleanCache(),
 * which clears the whole store. Keep the live value and re-seed from it.
 */
export function refreshForceScenesOpenFromStorage(): void {
  const stored = getStoredForceScenesOpen();
  if (stored === null) {
    // Nothing to preserve at the default — a fresh boot resolves to exactly this, and skipping
    // the write keeps standalone/online review byte-identical for users who never touched the row.
    // The default is platform-dependent now, so the comparison asks for THIS host's default: on
    // macOS a live `true` IS the default and needs no mirror, while a live `false` is the user's
    // own answer and has to be written back.
    if (currentForceScenesOpen !== defaultForceScenesOpen()) writeMirror(currentForceScenesOpen);
    return;
  }
  if (stored === currentForceScenesOpen) return;
  currentForceScenesOpen = stored;
  listeners.forEach((fn) => fn());
}

let reconcileListenersAttached = false;

/** Boot entry point — called once from main.tsx, right after initTileGrowth() (tests call it
 *  repeatedly; the reconcile listeners attach only once). */
export function initForceScenesOpen(): void {
  // URL param FIRST: in embedded mode Unity appends it on every load and it is the only
  // authoritative value available at boot (the catalog is pull-only). Then the localStorage
  // mirror, then the platform default (defaultForceScenesOpen — ON for the macOS app only, and
  // last in the chain precisely so a stored answer always beats it).
  const paramValue = coerceForceScenesOpen(readParam(FORCE_SCENES_OPEN_PARAM));
  currentForceScenesOpen = paramValue ?? getStoredForceScenesOpen() ?? defaultForceScenesOpen();
  // Re-seed the mirror from whatever native just told us, so the OTHER same-origin document
  // (the kept-alive Projects webview) agrees with Unity on its next reconcile.
  if (paramValue !== null) writeMirror(currentForceScenesOpen);
  if (!reconcileListenersAttached) {
    try {
      window.addEventListener('storage', (e) => {
        if (e.key === null || e.key === FORCE_SCENES_OPEN_STORAGE_KEY) refreshForceScenesOpenFromStorage();
      });
      // The parked Projects webview is restored in place (no reload, no remount), and
      // WKWebView's cross-document 'storage' delivery is unreliable — re-reading on the way back
      // in is what makes a value picked on the board show up here. Both events: an embedded
      // WKWebView can regain focus without a visibilitychange.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') refreshForceScenesOpenFromStorage();
      });
      window.addEventListener('focus', () => refreshForceScenesOpenFromStorage());
      reconcileListenersAttached = true;
    } catch {
      /* non-browser environment */
    }
  }
}
